// Survey question templates — pure functions that return raw HTML strings
// (rendered inside the shadow root) plus a `read()` function that pulls the
// answer back out of the DOM.
import type { SurveyQuestion } from '@layers/core-wasm';

export interface QuestionTemplate {
  /** HTML markup for the question body (no wrapping container — caller adds it). */
  html: string;
  /**
   * Read the current answer value from the rendered question. Returns null
   * if no answer has been provided yet.
   *
   * `root` is the survey's shadow root — every input is queried inside it
   * so consumer-page CSS can never leak in or out.
   */
  read: (root: ShadowRoot) => unknown;
}

const escape = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export function renderQuestion(question: SurveyQuestion): QuestionTemplate {
  switch (question.type) {
    case 'nps':
      return renderNps(question);
    case 'csat':
      return renderCsat(question);
    case 'ces':
      return renderCes(question);
    case 'multiple_choice':
      return renderMultipleChoice(question);
    case 'rating':
      return renderRating(question);
    case 'open_text':
      return renderOpenText(question);
    case 'link':
      return renderLink(question);
    default:
      // Defensive fallback — render text-only.
      return {
        html: `<p class="am-text">${escape(question.text)}</p>`,
        read: () => null
      };
  }
}

function renderScale(
  question: SurveyQuestion,
  defaultMin: number,
  defaultMax: number,
  className: string
): QuestionTemplate {
  const min = question.scale_min ?? defaultMin;
  const max = question.scale_max ?? defaultMax;
  const buttons: string[] = [];
  for (let i = min; i <= max; i++) {
    buttons.push(
      `<button type="button" class="am-scale-btn" data-q="${escape(question.id)}" data-v="${i}">${i}</button>`
    );
  }
  return {
    html: `
      <div class="am-q ${className}">
        <p class="am-text">${escape(question.text)}</p>
        <div class="am-scale">${buttons.join('')}</div>
      </div>
    `,
    read: (root) => {
      const selected = root.querySelector<HTMLButtonElement>(
        `.am-scale-btn[data-q="${cssEscape(question.id)}"].am-selected`
      );
      if (!selected) return null;
      const v = selected.getAttribute('data-v');
      return v !== null ? Number(v) : null;
    }
  };
}

function renderNps(question: SurveyQuestion): QuestionTemplate {
  return renderScale(question, 0, 10, 'am-q-nps');
}

function renderCsat(question: SurveyQuestion): QuestionTemplate {
  return renderScale(question, 1, 5, 'am-q-csat');
}

function renderCes(question: SurveyQuestion): QuestionTemplate {
  return renderScale(question, 1, 7, 'am-q-ces');
}

function renderRating(question: SurveyQuestion): QuestionTemplate {
  // 1..=5 stars rendered as scale buttons with star symbol.
  const min = question.scale_min ?? 1;
  const max = question.scale_max ?? 5;
  const buttons: string[] = [];
  for (let i = min; i <= max; i++) {
    buttons.push(
      `<button type="button" class="am-star-btn" data-q="${escape(question.id)}" data-v="${i}" aria-label="${i} star${i === 1 ? '' : 's'}">★</button>`
    );
  }
  return {
    html: `
      <div class="am-q am-q-rating">
        <p class="am-text">${escape(question.text)}</p>
        <div class="am-stars">${buttons.join('')}</div>
      </div>
    `,
    read: (root) => {
      const selected = root.querySelector<HTMLButtonElement>(
        `.am-star-btn[data-q="${cssEscape(question.id)}"].am-selected`
      );
      if (!selected) return null;
      const v = selected.getAttribute('data-v');
      return v !== null ? Number(v) : null;
    }
  };
}

function renderMultipleChoice(question: SurveyQuestion): QuestionTemplate {
  const options = question.options ?? [];
  // Multi-select if any option count >0 — for now use radio (single). The
  // schema allows extending to checkbox; we render single-select MVP.
  const items = options
    .map((opt, i) => {
      const id = `am-opt-${question.id}-${i}`;
      return `
        <label class="am-mc-option" for="${escape(id)}">
          <input type="radio" id="${escape(id)}" name="am-mc-${escape(question.id)}" value="${escape(opt)}" />
          <span>${escape(opt)}</span>
        </label>`;
    })
    .join('');
  return {
    html: `
      <div class="am-q am-q-mc">
        <p class="am-text">${escape(question.text)}</p>
        <div class="am-mc-options">${items}</div>
      </div>
    `,
    read: (root) => {
      const radio = root.querySelector<HTMLInputElement>(
        `input[name="am-mc-${cssEscape(question.id)}"]:checked`
      );
      return radio ? radio.value : null;
    }
  };
}

function renderOpenText(question: SurveyQuestion): QuestionTemplate {
  return {
    html: `
      <div class="am-q am-q-text">
        <label class="am-text" for="am-text-${escape(question.id)}">${escape(question.text)}</label>
        <textarea id="am-text-${escape(question.id)}" class="am-textarea" data-q="${escape(question.id)}" rows="4" placeholder="Type your answer…"></textarea>
      </div>
    `,
    read: (root) => {
      const ta = root.querySelector<HTMLTextAreaElement>(
        `textarea[data-q="${cssEscape(question.id)}"]`
      );
      const v = ta?.value.trim();
      return v || null;
    }
  };
}

function renderLink(question: SurveyQuestion): QuestionTemplate {
  const label = question.link_label ?? 'Open';
  const url = question.link_url ?? '#';
  return {
    html: `
      <div class="am-q am-q-link">
        <p class="am-text">${escape(question.text)}</p>
        <a class="am-cta-link" href="${escape(url)}" target="_blank" rel="noopener noreferrer" data-q="${escape(question.id)}">${escape(label)}</a>
      </div>
    `,
    // Link "answers" with `true` if the link was tapped; the popup wires up
    // the click event. For MVP we just submit `true` on submit unconditionally
    // so the response shape is consistent.
    read: () => true
  };
}

/**
 * Minimal CSS.escape polyfill — escapes a string so it's safe to use inside
 * a CSS attribute selector. Modern browsers expose `CSS.escape`, but we
 * include this fallback so the templates work in older runtimes too.
 */
function cssEscape(s: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(s);
  }
  return s.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}
